import { useEffect } from 'react';
import { Upload } from 'lucide-react';
import { Label } from '@hqo/ui-atoms';
import { useIntl } from 'react-intl';
import { useUploadVisitorsCsv } from '../../hooks/use-upload-visitors-csv';
import type { Visitor } from '../../../../types/form-data.types';
import type { VisitStepFieldsConfig } from '../../../../hooks/use-ui-metadata-field-config.hook';
import type { CsvUploadState } from './visitor-csv-upload-section';

interface VisitorCsvUploadButtonProps {
  onSuccess: (visitors: Visitor[]) => void;
  onStateChange?: (state: CsvUploadState) => void;
  fieldsConfig: VisitStepFieldsConfig;
}

export const VisitorCsvUploadButton = ({
  onSuccess,
  onStateChange,
  fieldsConfig,
}: VisitorCsvUploadButtonProps): JSX.Element => {
  const { formatMessage } = useIntl();

  const { handleFileUpload, uploadedFileName, visitorCount, uploadError, validationError } = useUploadVisitorsCsv({
    onSuccess,
    fieldsConfig,
  });

  // Notify a parent component about state changes
  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        fileName: uploadedFileName,
        visitorCount,
        error: uploadError,
        validationError,
      });
    }
  }, [uploadedFileName, visitorCount, uploadError, validationError, onStateChange]);

  const uploadLabel = formatMessage({ id: 'createVisitFormV2.visitStep.uploadCSV', defaultMessage: 'Upload CSV' });

  return (
    <>
      <input type="file" id="csvUpload" accept=".csv" className="hidden" onChange={handleFileUpload} />
      <Label htmlFor="csvUpload" className="cursor-pointer flex-1">
        <div className="flex items-center justify-center w-full h-10 px-5 py-2 text-sm font-medium bg-background border border-input rounded-xl hover:bg-accent hover:text-accent-foreground transition-colors">
          <Upload className="h-4 w-4 mr-2" />
          {uploadedFileName || uploadLabel}
        </div>
      </Label>
    </>
  );
};
